import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Zap, UserX } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

const CAT_IMGS = {
  Music: "/assets/cats/music.jpg",
  Dance: "/assets/cats/dance.jpg",
  Comedy: "/assets/cats/comedy.jpg",
  Astro: "/assets/cats/astro.jpg",
  "PK Battle": "/assets/cats/pk-battle.jpg",
  Gaming: "/assets/cats/gaming.jpg",
};
const TAG_EMOJI = { "#Azadi": "🇮🇳", "#SufiNight": "🔥", "#PKBattle": "⚔️", "#Bollywood": "🎬" };

export default function Explore() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/explore").then(({ data: d }) => setData(d)).catch(() => setData({ categories: [], trending: [], rising: [] }));
  }, []);

  const follow = async (id) => {
    try {
      const { data: res } = await api.post(`/users/${id}/follow`);
      setData((d) => ({ ...d, rising: d.rising.map((u) => (u.id === id ? { ...u, is_following: res.following } : u)) }));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="min-h-screen max-w-md mx-auto pb-28" data-testid="explore-page">
      <header className="px-4 pt-5 pb-3">
        <p className="text-white text-sm font-bold tracking-[0.3em] text-center mb-4" data-testid="explore-logo">EXPLORE</p>
        <button data-testid="explore-search-bar" onClick={() => navigate("/search")}
          className="w-full flex items-center gap-2.5 glass-chip px-4 py-3 text-[#6E6683] text-[13px]">
          <Search size={15} /> Search creators, categories…
        </button>
      </header>

      <div className="px-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6" data-testid="trending-tags">
          {(data?.trending || []).map((t) => (
            <button key={t} onClick={() => navigate("/search")}
              className="shrink-0 glass-chip px-3.5 py-2 text-xs font-bold text-white/90">
              {TAG_EMOJI[t] ? `${TAG_EMOJI[t]} ` : ""}{t}
            </button>
          ))}
        </div>

        <h2 className="text-white text-lg font-bold mb-3">Categories <span className="text-[#FFD86B]">✦</span></h2>
        <div className="grid grid-cols-2 gap-3 mb-7" data-testid="explore-categories">
          {data === null && Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton rounded-[20px] aspect-[16/10]" />)}
          {data?.categories.map((c, i) => (
            <motion.button key={c.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`explore-cat-${c.name.toLowerCase().replace(" ", "-")}`}
              onClick={() => navigate("/", { state: { cat: c.name } })}
              className="relative rounded-[20px] overflow-hidden aspect-[16/10] text-left active:scale-[0.98] transition-transform bg-[#1E1830]">
              {CAT_IMGS[c.name] && <img src={CAT_IMGS[c.name]} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 flex items-center justify-between">
                <p className="text-white text-sm font-extrabold uppercase tracking-wide">{c.name}</p>
                <p className="text-[10px] text-white/85 font-semibold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B5C] pulse-live inline-block" /> {c.live} live
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        <h2 className="text-white text-lg font-bold mb-3">Rising Stars</h2>
        <div className="space-y-2.5 mb-6" data-testid="rising-stars">
          {(data?.rising || []).map((u) => (
            <div key={u.id} className="bg-[#14101F] border border-white/[0.06] rounded-[20px] px-3.5 py-3 flex items-center gap-3">
              <button onClick={() => navigate(`/profile/${u.id}`)} className="relative shrink-0">
                <div className="rounded-full p-[2px]" style={{ background: "conic-gradient(from 200deg, #FF2E97, #A03CF5, #FFD86B, #FF2E97)" }}>
                  <Avatar src={u.avatar} size={44} />
                </div>
              </button>
              <div className="flex-1 min-w-0" onClick={() => navigate(`/profile/${u.id}`)} role="button">
                <p className="text-white text-[13px] font-bold truncate">{u.name} <span className="text-[#A03CF5]">✔</span></p>
                <p className="text-[11px] text-[#6E6683]">{(u.followers_count || 0).toLocaleString("en-IN")} fans</p>
              </div>
              {u.live_stream_id && (
                <span className="shrink-0 bg-[#FF3B5C] text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full pulse-live">LIVE</span>
              )}
              {u.live_stream_id ? (
                <button data-testid={`rising-watch-${u.id}`} onClick={() => navigate(`/stream/${u.live_stream_id}`)}
                  className="shrink-0 gradient-primary text-white text-[11px] font-bold px-4 py-1.5 rounded-full active:scale-95 transition-transform">Watch</button>
              ) : (
                <button data-testid={`rising-follow-${u.id}`} onClick={() => follow(u.id)}
                  className={`shrink-0 text-[11px] font-bold px-4 py-1.5 rounded-full active:scale-95 transition-transform ${u.is_following ? "bg-white/10 text-[#B8B0C9]" : "gradient-primary text-white"}`}>
                  {u.is_following ? "Following" : "+ Follow"}
                </button>
              )}
            </div>
          ))}
          {data && data.rising?.length === 0 && (
            <div className="text-center py-6 text-[#6E6683] text-xs"><UserX size={20} className="mx-auto mb-1.5" />No rising stars yet</div>
          )}
        </div>

        <button data-testid="explore-random-match" onClick={() => navigate("/discover")}
          className="w-full rounded-[20px] px-4 py-4 flex items-center gap-3 text-left active:scale-[0.98] transition-transform mb-2"
          style={{ background: "linear-gradient(120deg, rgba(160,60,245,0.18), rgba(30,24,48,0.9))", border: "1px solid rgba(160,60,245,0.55)", boxShadow: "0 0 24px rgba(160,60,245,0.25)" }}>
          <span className="w-10 h-10 rounded-2xl bg-[#A03CF5]/25 flex items-center justify-center shrink-0"><Zap size={18} className="text-[#CFA3FF]" /></span>
          <span>
            <span className="block text-white text-sm font-extrabold">Random Match ⚡</span>
            <span className="block text-[11px] text-[#B8B0C9]">Jump into a surprise room</span>
          </span>
        </button>
      </div>
      <BottomNav />
    </div>
  );
}
